<script>
import moment from "moment";
import axios from '@/utils/axios';
import "daterangepicker/daterangepicker.js";
import "daterangepicker/daterangepicker.css";
import DateRangePicker from "daterangepicker";
import Vue3ChartJs from "@j-t-mcc/vue3-chartjs";

export default {
  inject: ['setLoading'],
  components: { Vue3ChartJs },
  data() {
    return {
      errors: {},
      title: "Reports",
      text: "Payment",
      text1: "Payment Report",
      app_currency: '',
      filter: {
        from_date: '',
        to_date: '',
        user_id: '',
        customer_id: '',
        payment_method: '',
        search_keyword: '',
      },
      summary: {},
      data: [],
      users: [],
      customers: [],
      selectedCustomer: null,
      customerSearchLoading: false,
      pagination: {
        current_page: 1,
        per_page: 25,
        last_page: 1,
        total: 0,
        links: []
      },
    };
  },

  computed: {
    serialNo() {
      return (this.pagination.current_page - 1) * this.pagination.per_page;
    },

    // Handles the upi/UPI casing mismatch coming from the backend
    paymentMethodBreakdown() {
      const counts = this.summary.payment_method_counts || {};
      const upiTotal = (parseInt(counts.upi || 0)) + (parseInt(counts.UPI || 0));
      const map = [
        { key: 'cash', label: 'Cash', icon: 'ti-cash', color: 'success', count: parseInt(counts.cash || 0) },
        { key: 'upi', label: 'UPI', icon: 'ti-qrcode', color: 'primary', count: upiTotal },
        { key: 'card', label: 'Card', icon: 'ti-credit-card', color: 'info', count: parseInt(counts.card || 0) },
        { key: 'bank_transfer', label: 'Bank Transfer', icon: 'ti-building-bank', color: 'warning', count: parseInt(counts.bank_transfer || 0) },
        { key: 'cheque', label: 'Cheque', icon: 'ti-file-invoice', color: 'secondary', count: parseInt(counts.cheque || 0) },
      ];
      return map.filter(m => m.count > 0);
    },

    paymentMethodChart() {
      const colorMap = { success: '#10b981', primary: '#4f46e5', info: '#06b6d4', warning: '#f59e0b', secondary: '#64748b' };
      const labels = this.paymentMethodBreakdown.map(m => m.label);
      const values = this.paymentMethodBreakdown.map(m => m.count);
      const colors = this.paymentMethodBreakdown.map(m => colorMap[m.color]);
      return {
        type: 'doughnut',
        data: {
          labels,
          datasets: [{
            data: values,
            backgroundColor: colors,
            borderWidth: 2,
            hoverOffset: 6,
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'bottom', labels: { padding: 15, usePointStyle: true, pointStyle: 'circle' } }
          },
          cutout: '65%',
        }
      };
    },

    topCustomersChart() {
      const map = {};
      (this.data || []).forEach(row => {
        const name = row.customer?.name || 'Unknown';
        map[name] = (map[name] || 0) + parseFloat(row.amount || 0);
      });
      const labels = Object.keys(map);
      const values = Object.values(map);
      const colors = ['#4f46e5', '#06b6d4', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6'];
      return {
        type: 'bar',
        data: {
          labels,
          datasets: [{
            label: 'Amount Received',
            data: values,
            backgroundColor: labels.map((_, i) => colors[i % colors.length]),
            borderRadius: 6,
            barThickness: 40,
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: {
            y: { beginAtZero: true, grid: { color: '#f3f4f6' } },
            x: { grid: { display: false } }
          }
        }
      };
    },
  },

  async mounted() {
    this.app_currency = await this.$appCurrency();
    this.getDropdowns();

    const start = moment().subtract(6, "days");
    const end = moment();

    this.filter.from_date = start.format("YYYY-MM-DD");
    this.filter.to_date = end.format("YYYY-MM-DD");

    new DateRangePicker(
      this.$refs.dateRangeInput,
      {
        startDate: start,
        endDate: end,
        autoUpdateInput: false,
        locale: { format: "DD/MM/YYYY", separator: " - ", cancelLabel: "Clear" },
        ranges: {
          Today: [moment(), moment()],
          Yesterday: [moment().subtract(1, "days"), moment().subtract(1, "days")],
          "Last 7 Days": [moment().subtract(6, "days"), moment()],
          "Last 30 Days": [moment().subtract(29, "days"), moment()],
          "This Month": [moment().startOf("month"), moment().endOf("month")],
          "Last Month": [
            moment().subtract(1, "month").startOf("month"),
            moment().subtract(1, "month").endOf("month"),
          ],
        }
      },
      (start, end) => {
        this.filter.from_date = start.format("YYYY-MM-DD");
        this.filter.to_date = end.format("YYYY-MM-DD");
        this.$refs.dateRangeInput.value = start.format("DD/MM/YYYY") + " - " + end.format("DD/MM/YYYY");
        this.filterReport();
      }
    );

    this.filterReport();
  },

  methods: {
    async filterReport(page = 1) {
      this.errors = {};
      this.setLoading(true);
      try {
        const payload = {
          page,
          date_from: this.filter.from_date,
          date_to: this.filter.to_date,
        };
        if (this.filter.user_id) payload.user_id = this.filter.user_id;
        if (this.filter.customer_id) payload.customer_id = this.filter.customer_id;
        if (this.filter.payment_method) payload.payment_method = this.filter.payment_method;
        if (this.filter.search_keyword.trim()) payload.search_keyword = this.filter.search_keyword.trim();

        const query_string = new URLSearchParams(payload).toString();
        const response = await axios.get(`/reports/payments?${query_string}`);
        this.summary = response.data.data.summary || {};
        this.data = response.data.data.rows.data;
        this.pagination = {
          current_page: response.data.data.rows.current_page,
          per_page: response.data.data.rows.per_page,
          last_page: response.data.data.rows.last_page,
          total: response.data.data.rows.total,
          links: response.data.data.rows.links
        };
      } catch (error) {
        if (error.type == 'validation') {
          this.errors = error.errors;
        } else {
          this.$swalToast(error.message, 'error');
        }
      } finally {
        this.setLoading(false);
      }
    },

    async getDropdowns() {
      try {
        const userRes = await axios.get('/users/all-users');
        this.users = userRes.data.data;
      } catch (error) {
        console.log('Dropdown load error:', error.message);
      }
    },

    async onCustomerSearch(search) {
      if (!search || search.length < 2) return;
      this.customerSearchLoading = true;
      try {
        const res = await axios.get('/customers/dropdown', { params: { search_keyword: search } });
        this.customers = res.data.data;
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.customerSearchLoading = false;
      }
    },

    onCustomerSelected(row) {
      this.filter.customer_id = row?.id || '';
      this.selectedCustomer = row;
      this.filterReport();
    },

    async exportCSV() {
      this.setLoading(true);
      try {
        const payload = { export: 'csv' };
        if (this.filter.from_date) payload.date_from = this.filter.from_date;
        if (this.filter.to_date) payload.date_to = this.filter.to_date;
        if (this.filter.user_id) payload.user_id = this.filter.user_id;
        if (this.filter.customer_id) payload.customer_id = this.filter.customer_id;
        if (this.filter.payment_method) payload.payment_method = this.filter.payment_method;
        if (this.filter.search_keyword.trim()) payload.search_keyword = this.filter.search_keyword.trim();

        const query_string = new URLSearchParams(payload).toString();
        const response = await axios.get(`/reports/payments?${query_string}`, {
          responseType: 'blob',
        });

        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `payment-report-${this.filter.from_date}-to-${this.filter.to_date}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      } catch (error) {
        this.$swalToast('Failed to export CSV', 'error');
      } finally {
        this.setLoading(false);
      }
    },

    changePage(url) {
      if (!url) return;
      const page = new URL(url).searchParams.get("page");
      this.filterReport(page);
    },

    hasActiveFilter() {
      return this.filter.user_id || this.filter.customer_id || this.filter.payment_method || this.filter.search_keyword || this.filter.from_date || this.filter.to_date;
    },

    clearFilter() {
      this.filter.user_id = '';
      this.filter.customer_id = '';
      this.selectedCustomer = null;
      this.filter.payment_method = '';
      this.filter.search_keyword = '';
      this.filter.from_date = '';
      this.filter.to_date = '';
      this.$refs.dateRangeInput.value = '';
      this.filterReport();
    },

    paymentBadgeClass(method) {
      const map = {
        Cash: 'bg-success-subtle text-success',
        UPI: 'bg-primary-subtle text-primary',
        Card: 'bg-info-subtle text-info',
        'Bank Transfer': 'bg-warning-subtle text-warning',
        Cheque: 'bg-secondary-subtle text-secondary',
      };
      return map[method] || 'bg-secondary-subtle text-secondary';
    },

    formatDate(dateStr) {
      if (!dateStr) return '-';
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    },

    formatDateTime(dateStr) {
      if (!dateStr) return '-';
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
        + ', ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
    },

    formatCurrency(val) {
      return parseFloat(val || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR' });
    },

    changePctClass(val) {
      const num = parseFloat(val || 0);
      if (num > 0) return 'text-success';
      if (num < 0) return 'text-danger';
      return 'text-muted';
    },

    changePctIcon(val) {
      const num = parseFloat(val || 0);
      if (num > 0) return 'ti ti-trending-up';
      if (num < 0) return 'ti ti-trending-down';
      return 'ti ti-minus';
    },
  }
};
</script>

<style scoped>
/* Amount search box */
.p-search-wrap {
  display: flex; align-items: center;
  border: 1.5px solid #e2e8f0; border-radius: 8px;
  overflow: hidden; background: #fff;
}
.p-search-wrap:focus-within { border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99,102,241,.1); }
.p-search-input {
  border: none; outline: none; height: 34px;
  padding: 0 10px; font-size: 12.5px; color: #1e293b;
  width: 170px; background: transparent;
}
.p-search-btn {
  width: 36px; height: 36px; background: #0f172a;
  border: none; color: #fff; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  font-size: 14px; transition: background .15s;
}
.p-search-btn:hover { background: #1e293b; }
</style>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">

      <!-- Breadcrumb -->
      <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div class="d-flex my-xl-auto right-content align-items-center flex-wrap">
          <button class="btn btn-outline-primary d-flex align-items-center" @click="exportCSV" :disabled="!data || !data.length">
            <i class="ti ti-file-type-csv me-2"></i>Export CSV
          </button>
        </div>
      </div>

      <!-- Summary Cards -->
      <div class="row g-3 mb-3" v-if="summary && summary.total_payments">
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Total Payments</span>
              <span class="fw-bold text-dark" style="font-size:20px;">{{ summary.total_payments }}</span>
              <span v-if="summary.payment_change_pct != null" :class="changePctClass(summary.payment_change_pct)" style="font-size:11px;">
                <i :class="changePctIcon(summary.payment_change_pct)" style="font-size:12px;"></i> {{ Math.abs(summary.payment_change_pct) }}%
              </span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Total Amount</span>
              <span class="fw-bold text-success" style="font-size:20px;">{{ formatCurrency(summary.total_amount) }}</span>
              <span v-if="summary.amount_change_pct != null" :class="changePctClass(summary.amount_change_pct)" style="font-size:11px;">
                <i :class="changePctIcon(summary.amount_change_pct)" style="font-size:12px;"></i> {{ Math.abs(summary.amount_change_pct) }}%
              </span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Unique Customers</span>
              <span class="fw-bold text-dark" style="font-size:20px;">{{ summary.unique_customers }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Unique Users</span>
              <span class="fw-bold text-dark" style="font-size:20px;">{{ summary.unique_users }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Avg Payment Value</span>
              <span class="fw-bold text-dark" style="font-size:20px;">{{ formatCurrency(summary.avg_payment_value) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Payment Method Breakdown Cards -->
      <div class="row g-3 mb-3" v-if="paymentMethodBreakdown.length > 1">
        <div class="col" v-for="pm in paymentMethodBreakdown" :key="pm.key">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3">
              <div class="d-flex align-items-center justify-content-between mb-1">
                <span class="d-flex align-items-center">
                  <i class="ti me-2" :class="[pm.icon, 'text-' + pm.color]" style="font-size:18px;"></i>
                  <span class="fw-semibold" style="font-size:13px;">{{ pm.label }}</span>
                </span>
                <span class="badge rounded-pill" :class="'bg-' + pm.color + '-subtle text-' + pm.color">{{ pm.count }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Charts -->
      <div class="row g-3 mb-3" v-if="data && data.length">
        <div class="col-md-8">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-header border-0 pb-0">
              <h6 class="fw-bold mb-0"><i class="ti ti-chart-bar me-1 text-primary"></i>Amount by Customer</h6>
            </div>
            <div class="card-body" style="height:280px;">
              <vue3-chart-js v-bind="topCustomersChart" :key="'cust-'+data.length" />
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-header border-0 pb-0">
              <h6 class="fw-bold mb-0"><i class="ti ti-chart-donut me-1 text-primary"></i>By Payment Method</h6>
            </div>
            <div class="card-body" style="height:280px;">
              <vue3-chart-js v-bind="paymentMethodChart" :key="'pm-'+data.length" />
            </div>
          </div>
        </div>
      </div>

      <!-- Filters -->
      <div class="card border-0">
        <div class="card-body d-flex align-items-center justify-content-center flex-wrap pb-1">
          <div class="d-flex align-items-center flex-wrap mb-1 gap-2">
            <div class="me-3" v-if="hasActiveFilter()">
              <a href="javascript:void(0);" class="text-danger" @click="clearFilter()"><i class="ti ti-filter-x"></i></a>
            </div>
            <div class="me-3">
              <div class="input-icon-end position-relative">
                <input type="text" class="form-control date-range bookingrange" ref="dateRangeInput" placeholder="dd/mm/yyyy - dd/mm/yyyy">
                <span class="input-icon-addon"><i class="ti ti-chevron-down"></i></span>
              </div>
            </div>
            <div class="me-3">
              <vue-select
                v-model="selectedCustomer"
                :options="customers"
                :loading="customerSearchLoading"
                :filterable="false"
                @search="onCustomerSearch"
                label="name"
                placeholder="Search customer..."
                :clearable="true"
                @update:modelValue="onCustomerSelected"
                style="min-width: 200px;"
              ></vue-select>
            </div>
            <div class="me-3">
              <vue-select
                v-model="filter.user_id"
                :options="users"
                label="name"
                :reduce="u => u.id"
                placeholder="Collected By"
                :clearable="true"
                @update:modelValue="filterReport()"
                style="min-width: 180px;"
              ></vue-select>
            </div>
            <div class="me-3">
              <vue-select
                v-model="filter.payment_method"
                :options="['Cash', 'UPI', 'Card', 'Bank Transfer', 'Cheque']"
                placeholder="Payment Method"
                :clearable="true"
                @update:modelValue="filterReport()"
                style="min-width: 180px;"
              ></vue-select>
            </div>
            <div class="me-3">
              <div class="p-search-wrap">
                <input type="text" class="p-search-input" v-model="filter.search_keyword"
                  placeholder="Enter Amount..." @keyup.enter="filterReport()" />
                <button class="p-search-btn" type="button" @click="filterReport()">
                  <i class="ti ti-search"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="row">
        <div class="col-lg-12">
          <div class="card-body card-table-wrapper">
            <div class="custom-datatable-filter table-responsive">
              <table class="card-table" v-if="data && data.length">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Date</th>
                    <th>Customer</th>
                    <th>Collected By</th>
                    <th>Payment Method</th>
                    <th>Transaction / Ref</th>
                    <th>Notes</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, index) in data" :key="row.id">
                    <td>{{ serialNo + index + 1 }}.</td>
                    <td>
                      <span>{{ formatDate(row.payment_date) }}</span>
                      <small class="text-muted d-block" style="font-size:10px;">{{ formatDateTime(row.created_at).split(', ')[1] }}</small>
                    </td>
                    <td>
                      <strong class="text-dark">{{ row.customer?.name }}</strong>
                      <small v-if="row.customer?.company_name" class="text-muted d-block" style="font-size:11px;">{{ row.customer.company_name }}</small>
                    </td>
                    <td>
                      <small class="text-muted">{{ row.user?.name || '-' }}</small>
                    </td>
                    <td>
                      <span class="badge rounded-pill" :class="paymentBadgeClass(row.payment_method)">{{ row.payment_method }}</span>
                    </td>
                    <td>
                      <small class="text-muted">{{ row.transaction_id || row.gateway_order_id || '-' }}</small>
                    </td>
                    <td>
                      <small class="text-muted">{{ row.notes || '-' }}</small>
                    </td>
                    <td><strong>{{ formatCurrency(row.amount) }}</strong></td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="p-3 text-center text-muted">No records found.</div>

              <!-- Pagination -->
              <nav v-if="pagination.links && pagination.links.length > 3" class="m-3">
                <ul class="pagination pagination-sm">
                  <li v-for="(link, index) in pagination.links" :key="index"
                    :class="['page-item', { active: link.active, disabled: !link.url }]">
                    <a class="page-link" href="javascript:void(0)" v-html="link.label" @click="changePage(link.url)"></a>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>

    </div>
    <app-footer></app-footer>
  </div>
</template>